import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { assistantApi } from '@/modules/assistant/services/assistant-api';
import { assistantQueryKeys } from '@/modules/assistant/hooks/query-keys';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';
import type { AssistantThreadInput } from '@/api/schemas/assistant';

/**
 * The caller's conversations, newest first.
 *
 * The server owns the history, so this is a read of the server's list rather than
 * a local cache of it. The list is sorted here — the API's order is not part of
 * the contract, and "newest first" is a decision the console makes.
 */
export function useAssistantThreads(scope: AssistantScope) {
  return useQuery({
    queryKey: assistantQueryKeys.threads(scope),
    queryFn: () => assistantApi.listThreads(scope),
    select: (threads) =>
      [...threads].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
  });
}

/**
 * Opens a conversation, anchored to a route.
 *
 * The thread list is invalidated afterwards so the new conversation appears in the
 * history without a reload.
 */
export function useCreateAssistantThread(scope: AssistantScope) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: AssistantThreadInput) => assistantApi.createThread(scope, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: assistantQueryKeys.threads(scope) });
    },
  });
}
